import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

const PLACEHOLDER = '/assets/no-photo.svg';

export default function ChatPage() {
  const { id: conversationId } = useParams();
  const { user, token }        = useAuthStore();
  const [conversations, setConversations] = useState([]);
  const [messages,      setMessages]      = useState([]);
  const [activeConv,    setActiveConv]    = useState(conversationId || null);
  const [input,         setInput]         = useState('');
  const [loading,       setLoading]       = useState(false);
  const bottomRef  = useRef(null);
  const pollRef    = useRef(null);

  // Load conversations (and refresh every 8s for unread counts / new chats)
  useEffect(() => {
    const loadConvs = () => api.get('/messages/conversations')
      .then(({ data }) => setConversations(data.conversations || []))
      .catch(() => {});
    loadConvs();
    const t = setInterval(loadConvs, 8000);
    return () => clearInterval(t);
  }, []);

  // Load + poll messages when a conversation is selected (replaces websockets)
  useEffect(() => {
    if (!activeConv) return;
    let cancelled = false;
    setLoading(true);
    const load = (initial) => api.get(`/messages/${activeConv}`).then(({ data }) => {
      if (cancelled) return;
      const next = data.messages || [];
      setMessages(prev => {
        if (initial || next.length !== prev.length) scrollBottom();
        return next;
      });
    }).catch(() => {}).finally(() => { if (initial && !cancelled) setLoading(false); });
    load(true);
    pollRef.current = setInterval(() => load(false), 4000);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [activeConv]);

  const scrollBottom = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

  const sendMessage = async () => {
    if (!input.trim() || !activeConv) return;
    const body = input.trim();
    setInput('');
    try {
      const { data } = await api.post(`/messages/${activeConv}`, { body });
      setMessages(prev => [...prev, data.message]);
      scrollBottom();
    } catch (err) {
      setInput(body);
      alert(err.response?.data?.message || 'Failed to send');
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const selectedConv = conversations.find(c => c.conversation_id === activeConv);

  return (
    <div className="min-h-screen bg-cream-100 flex flex-col">
      <Navbar />
      <div
        className="flex flex-1 max-w-7xl mx-auto w-full px-0 md:px-4 py-0 md:py-6 gap-4"
        style={{ height: 'calc(100vh - 4rem)' }}
      >

        {/* Conversation list — full width on mobile, hidden once a chat is open */}
        <div className={`w-full md:w-80 card md:flex-shrink-0 overflow-hidden flex-col rounded-none md:rounded-2xl ${
          activeConv ? 'hidden md:flex' : 'flex'
        }`}>
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-heading font-bold text-gray-900">Messages</h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm px-4">
                <p className="text-3xl mb-2">💬</p>
                No conversations yet. Send interest to start chatting!
              </div>
            ) : conversations.map(conv => (
              <button key={conv.conversation_id}
                onClick={() => setActiveConv(conv.conversation_id)}
                className={`w-full flex items-center gap-3 p-3 hover:bg-cream-100 text-left transition-colors ${
                  activeConv === conv.conversation_id ? 'bg-primary-50 border-r-2 border-primary' : ''
                }`}>
                <img src={conv.photo || PLACEHOLDER} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{conv.first_name} {conv.last_name}</p>
                  <p className="text-xs text-gray-400 truncate">{conv.last_message || 'Start the conversation'}</p>
                </div>
                {conv.unread_count > 0 && (
                  <span className="bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                    {conv.unread_count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Chat area — full width on mobile, shown only once a conversation is picked */}
        <div className={`flex-1 card flex-col overflow-hidden rounded-none md:rounded-2xl ${
          activeConv ? 'flex' : 'hidden md:flex'
        }`}>
          {!activeConv ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <p className="text-5xl mb-3">💌</p>
                <p>Select a conversation to start chatting</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat header */}
              {selectedConv && (
                <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                  <button onClick={() => setActiveConv(null)} className="md:hidden -ml-1 p-1 text-gray-500 flex-shrink-0">
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <img src={selectedConv.photo || PLACEHOLDER} alt="" className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-medium text-gray-900">{selectedConv.first_name} {selectedConv.last_name}</p>
                    <p className="text-xs text-gray-400">{selectedConv.district}</p>
                  </div>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loading ? (
                  <div className="flex justify-center py-10">
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : messages.map(msg => {
                  const isMine = msg.sender_id === user?.profileId;
                  return (
                    <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs lg:max-w-sm px-4 py-2 rounded-2xl text-sm ${
                        isMine
                          ? 'bg-primary text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}>
                        <p>{msg.body}</p>
                        <p className={`text-xs mt-1 ${isMine ? 'text-primary-200' : 'text-gray-400'}`}>
                          {new Date(msg.sent_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-gray-100 flex gap-2">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Type a message... (Enter to send)"
                  rows={1}
                  className="flex-1 input-field resize-none"
                />
                <button onClick={sendMessage} disabled={!input.trim()}
                  className="btn-primary p-2.5 rounded-lg disabled:opacity-40">
                  <PaperAirplaneIcon className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
